import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await api.get('/auth/me');
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setUser(user);
      setIsAuthenticated(true);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const logout = ({ azure = false } = {}) => {
    if (azure) {
      // Full Microsoft sign-out — go straight to the server endpoint so the browser
      // navigates away immediately. We deliberately do NOT touch React state first,
      // because that triggers a re-render of <ProtectedRoute /> which flashes the
      // /login page for an instant before the redirect kicks in. The new page load
      // (back from Microsoft) will reset all state naturally.
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];

      const apiBase = process.env.REACT_APP_API_URL || '/api';
      const url = `${apiBase}/auth/azure/logout`;
      try {
        if (window.top && window.top !== window.self) {
          // Embedded in dashboard iframe — break out, Microsoft's logout page
          // sets X-Frame-Options: deny and won't render inside a frame.
          window.top.location.replace(url);
          return;
        }
      } catch (_) {
        // Cross-origin top frame access denied — fall through to local navigation
      }
      window.location.replace(url);
      return;
    }

    // Local-only logout (no Microsoft round-trip)
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
  };

  // Role-based permission helpers
  const isAdmin = () => user?.role === 'admin';
  const isManager = () => user?.role === 'manager';
  const isSales = () => user?.role === 'sales';
  const canDelete = () => ['admin', 'manager'].includes(user?.role);
  const canEditProducts = () => ['admin', 'manager','sales'].includes(user?.role);
  const canManageUsers = () => user?.role === 'admin';

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    checkAuth,
    // Permission helpers
    isAdmin,
    isManager,
    isSales,
    canDelete,
    canEditProducts,
    canManageUsers,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
